/* eslint-disable */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { escapeHtml } from './jobUtils';
import './JobSidebarMapWidget.css';

// Fix Leaflet default icon path issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const DEFAULT_CENTER = [13.7563, 100.5018];
const DEFAULT_ZOOM = 11;

const buildWidgetPin = (item) => {
  const rawSrc = item.avatar || item.logo || item.cover || '';
  const imgSrc = String(rawSrc).trim();
  const hasImg = !!imgSrc;
  return L.divIcon({
    className: 'widget-map-pin-wrap',
    html: `
      <div class="widget-map-pin">
        ${hasImg ? `<img class="widget-pin-img" src="${imgSrc}" alt="${item.name || item.company || ''}" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />` : ''}
        <div class="widget-pin-placeholder" style="${hasImg ? 'display:none;' : 'display:flex;'}"><i class="mdi mdi-account"></i></div>
        <div class="widget-pin-pulse"></div>
      </div>
    `,
    iconSize: [36, 44],
    iconAnchor: [18, 44],
    popupAnchor: [0, -40],
  });
};

function JobSidebarMapWidget({
  jobs = [],
  items = null,
  mode = 'jobs',
  onOpenFullMap,
  onToast,
  onSelectCandidate = null,
}) {
  const navigate = useNavigate();
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const userMarkerRef = useRef(null);
  const userInteractedRef = useRef(false);
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;

  const [userLocationState, setUserLocationState] = useState('กำลังระบุตำแหน่ง...');
  const [isLocating, setIsLocating] = useState(false);
  const [userCoords, setUserCoords] = useState(null);

  const displayItems = items || jobs || [];
  const isCandidateMode = mode === 'candidates';

  const itemsWithCoords = useMemo(() => {
    return displayItems.filter((j) => typeof j.lat === 'number' && typeof j.lng === 'number');
  }, [displayItems]);

  const jobsKey = itemsWithCoords.map((j) => `${j.id}:${j.lat}:${j.lng}`).join('|');

  const locateUser = (forceCenter = false) => {
    if (!navigator.geolocation) {
      setUserLocationState('แสดงตำแหน่งกรุงเทพฯ');
      return;
    }

    setIsLocating(true);
    setUserLocationState('กำลังระบุตำแหน่งของคุณ...');

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const targetMap = mapInstanceRef.current;
        if (!targetMap) return;

        const { latitude, longitude } = pos.coords;
        setUserCoords([latitude, longitude]);

        if (userMarkerRef.current) {
          try {
            userMarkerRef.current.remove();
          } catch (_) {}
        }

        const userIcon = L.divIcon({
          className: 'widget-user-location-wrap',
          html: `
            <div class="widget-user-marker">
              <div class="user-core-dot"></div>
              <div class="user-pulse-ring"></div>
            </div>
          `,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });

        userMarkerRef.current = L.marker([latitude, longitude], { icon: userIcon })
          .bindPopup(' ตำแหน่งของคุณ')
          .addTo(targetMap);

        if (forceCenter || !userInteractedRef.current) {
          try {
            targetMap.setView([latitude, longitude], 12, { animate: true });
          } catch (_) {}
        }

        setUserLocationState('แสดงตำแหน่งใกล้คุณแล้ว');
        setIsLocating(false);
        if (forceCenter && onToastRef.current) onToastRef.current('ระบุตำแหน่งของคุณเรียบร้อยแล้ว');
      },
      () => {
        setIsLocating(false);
        setUserLocationState('แสดงแผนที่พื้นที่กรุงเทพฯ');
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  // 1. Initialize Leaflet Map ONCE
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapInstanceRef.current) return;

    if (mapContainerRef.current._leaflet_id) {
      delete mapContainerRef.current._leaflet_id;
    }

    const map = L.map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: false,
      attributionControl: false,
    });

    map.on('dragstart zoomstart movestart', () => {
      userInteractedRef.current = true;
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);
    mapInstanceRef.current = map;

    locateUser(false);

    setTimeout(() => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.invalidateSize();
        } catch (_) {}
      }
    }, 200);

    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch (_) {}
        mapInstanceRef.current = null;
      }
      markersLayerRef.current = null;
      userMarkerRef.current = null;
    };
  }, []);

  // 2. Update Markers Dynamically
  useEffect(() => {
    const map = mapInstanceRef.current;
    const layer = markersLayerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();

    const bounds = [];
    itemsWithCoords.forEach((item) => {
      const marker = L.marker([item.lat, item.lng], {
        icon: buildWidgetPin(item),
        title: isCandidateMode ? item.name : item.company,
      });

      const popupContent = document.createElement('div');
      popupContent.className = 'widget-map-popup';

      const title = isCandidateMode ? item.name : item.title;
      const subtitle = isCandidateMode ? item.title : item.company;
      const meta = isCandidateMode ? item.salaryRange || item.salary || item.province : item.salary;

      popupContent.innerHTML = `
        <div class="widget-popup-title">${escapeHtml(title)}</div>
        <div class="widget-popup-company">${escapeHtml(subtitle)}</div>
        <div class="widget-popup-salary">${escapeHtml(meta)}</div>
      `;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'widget-popup-btn';
      btn.textContent = isCandidateMode ? 'ดูโปรไฟล์' : 'ดูตำแหน่งงาน';
      btn.onclick = () => {
        if (isCandidateMode) {
          if (onSelectCandidate) {
            onSelectCandidate(item);
          } else if (onOpenFullMap) {
            onOpenFullMap();
          }
        } else {
          navigate(`/jobs/detail/${item.id}`);
        }
      };
      popupContent.appendChild(btn);

      marker.bindPopup(popupContent);
      marker.addTo(layer);
      bounds.push([item.lat, item.lng]);
    });

    if (bounds.length > 1 && !userCoords && !userInteractedRef.current) {
      try {
        map.fitBounds(bounds, { padding: [25, 25], maxZoom: 12 });
      } catch (_) {}
    }

    try {
      map.invalidateSize();
    } catch (_) {}
  }, [jobsKey, isCandidateMode]);

  const handleLocateClick = (e) => {
    e.stopPropagation();
    userInteractedRef.current = false;
    locateUser(true);
  };

  const handleExpand = (e) => {
    e?.stopPropagation();
    if (onOpenFullMap) onOpenFullMap(userCoords);
  };

  return (
    <div className='sidebar-box map-preview-box live-map-sidebar-box'>
      <div className='sidebar-map-header'>
        <div className='sidebar-map-title-row'>
          <h3>
            <i
              className={`mdi ${isCandidateMode ? 'mdi-account-group text-primary' : 'mdi-map-marker-radius text-primary'}`}
            ></i>{' '}
            {isCandidateMode ? 'ผู้หางานใกล้คุณ' : 'บริษัทรับสมัครใกล้คุณ'}
          </h3>
          <span className='live-badge'>
            <span className='live-dot'></span> สด
          </span>
        </div>
        <span className='user-loc-subtext'>{userLocationState}</span>
      </div>

      <div className='sidebar-live-map-wrapper'>
        <div className='sidebar-live-map-canvas' ref={mapContainerRef} />

        <div className='map-floating-overlay-controls'>
          <button
            type='button'
            className='btn-map-control-chip'
            onClick={handleLocateClick}
            disabled={isLocating}
            title='ค้นหาตำแหน่งของฉัน'
          >
            <i className={`mdi ${isLocating ? 'mdi-loading mdi-spin' : 'mdi-crosshairs-gps'}`}></i>
            {isLocating ? 'ระบุตำแหน่ง...' : 'ตำแหน่งฉัน'}
          </button>

          <button
            type='button'
            className='btn-map-control-chip btn-expand-chip'
            onClick={handleExpand}
            title='เปิดแผนที่แบบเต็มจอ'
          >
            <i className='mdi mdi-fullscreen'></i> เต็มจอ
          </button>
        </div>
      </div>

      <div className='sidebar-map-footer'>
        <span className='job-count-badge'>
          <i
            className={`mdi ${isCandidateMode ? 'mdi-account-group' : 'mdi-hospital-building'}`}
          ></i>{' '}
          {itemsWithCoords.length} {isCandidateMode ? 'โปรไฟล์ผู้หางานในระบบ' : 'สถานพยาบาลในระบบ'}
        </span>
        <button type='button' className='btn-map-view-full' onClick={handleExpand}>
          เปิดดูบนแผนที่ <i className='mdi mdi-arrow-right'></i>
        </button>
      </div>
    </div> 
  );
}

export default React.memo(JobSidebarMapWidget);
